import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, Input, Icon} from 'components';
import {usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.CO_BROWSING_DIALOG;

type Props = {
    open: boolean;
    url?: string;
    clientUrl?: string;
    isLoading?: boolean;
    isError?: boolean;
    onClose: (url?: string) => void;
};

export const CoBrowsingStartDialog: React.FC<Props> = ({open, url, clientUrl, isLoading, isError, onClose: onCloseProp}: Props) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);
    const [localUrl, setLocalUrl] = useState<string>('');

    useEffect(() => {
        if (open !== prevOpen && open && url) {
            setLocalUrl(url);
        }
    }, [open, prevOpen, url]);

    const useClientUrl = useCallback(() => {
        setLocalUrl(clientUrl);
    }, [clientUrl]);

    const onClose = useCallback((confirm: boolean) => {
        const data = confirm ? localUrl : null;
        onCloseProp(data);
    }, [localUrl, onCloseProp]);

    const onChange = useCallback((value: string) => {
        setLocalUrl(value);
    }, []);

    return (
        <ConfirmModal
            open={open}
            maxWidth='sm'
            title={t(T_KEYS.TITLE)}
            fullWidth
            okDisabled={!localUrl || isLoading}
            okText={t(T_KEYS.SUBMIT_BTN)}
            onClose={onClose}
        >
            <Input
                autoFocus
                value={localUrl}
                hasError={isError}
                errorMessage={t(T_KEYS.ERROR)}
                label={t(T_KEYS.TEXT_LABEL)}
                onFocus={(event) => event.target.setSelectionRange(localUrl.length, localUrl.length)}
                onChange={onChange}
            />
            {clientUrl &&
                <div className='tw-mt-5'>
                    <div>{t(T_KEYS.CLIENT_URL_TITLE)}</div>
                    <div className='tw-flex tw-justify-between tw-mt-1'>
                        <div className='tw-text-sm tw-text-break-word tw-mr-2'>{clientUrl}</div>
                        <Icon name='paper-clip2' primary button margin={false} size='lg' onClick={useClientUrl} />
                    </div>
                </div>}
        </ConfirmModal>
    );
};
